import {DynamicReferenceType} from "~/enums/dynamicReferenceType"
import Formula from "~/models/formula"

interface IFormSubmissionWysiwygValueOpts {
  evaluator: {
    evaluateFormula(scope: string, opts: { formula: string })
  }

  formatter(value: unknown, data: IPillData): string | number | boolean
}

interface IPillData {
  type: DynamicReferenceType
  formula?: string
  id?: string
}

/**
 * Use this class to turn an html string with <var> tags into one with their content replaces with formula evaluations
 * @example evaluate the result for the label_title of a paragraph form element with variables/formulae
 *   this.formulaEvaluator = new FormSubmissionConditionalRuleEvaluator<string, IFormElement>(this.formElement, 'label_title', this.conditionalRuleEvaluatorOptions)
 *   this.wysiwygValue = new FormSubmissionWysiwygValue({evaluator: this.formulaEvaluator})
 *   this.wysiwygValue.getValue(this.formElement.label_title, this.path)
 */
export default class FormSubmissionWysiwygValue {
  constructor(private opts: IFormSubmissionWysiwygValueOpts) {
  }

  /**
   * Extract out the data ids of every pill in the wysiwyg snippet
   * @param snippet {String} the html text to evaluate
   * @return {Array<String>} ids of variables for wysiwyg pills. e.g. form element client_ids
   */
  static parseClientIds(snippet: string): string[] {
    let $snippet: JQuery<HTMLParagraphElement>
    try {
      $snippet = $(snippet.startsWith('<') ? snippet : `<p>${snippet}</p>`)
    } catch (e) {
      $snippet = $(`<p>${snippet}</p>`)
    }
    const clientIds: Set<string> = new Set()
    $snippet.find('var.pill[data-dynamic]').each((varIndex, varEl) => {
      const $var = $(varEl)
      const data: IPillData = $var.data('dynamic')
      switch (data.type) {
        case DynamicReferenceType.FormElement:
        case DynamicReferenceType.ObjectModelProperty:
        case DynamicReferenceType.ObjectModelRecordMetadata:
          clientIds.add(data.id)
          break
        case DynamicReferenceType.Formula:
          Formula.parseClientIds(data.formula).forEach(clientIds.add, clientIds)
          break
      }
    })
    return Array.from(clientIds)
  }

  /**
   * Process the html snippet and return it but with var placeholder replaced with values
   * there are some hard-coded, malformed paragraph form element label titles for PAN, MMCC templates so catch error and return plain snippet
   *
   * @param snippet {String} the html text to evaluate
   * @param scope {String} the relevant path into the context of the evaluator on which to looks for values
   * @param opts
   * @param opts.evaluator anything that has an +evaluateFormula+ function that should return a value to insert into html snippet
   * @param opts.formatter
   * @private
   */
  private static getValue(snippet: string, scope: string, {
    evaluator,
    formatter,
  }: { evaluator: IFormSubmissionWysiwygValueOpts['evaluator'], formatter?: IFormSubmissionWysiwygValueOpts['formatter'] }): string {
    let $snippet
    formatter = formatter ?? (v => v.toString())
    try {
      $snippet = $(snippet.startsWith('<') ? snippet : `<p>${snippet}</p>`)
    } catch (e) {
      $snippet = $(`<p>${snippet}</p>`)
    }
    $snippet.find('var.pill[data-dynamic]').each((varIndex, varEl) => {
      const $var = $(varEl)
      const data: IPillData = $var.data('dynamic')
      switch (data.type) {
        case DynamicReferenceType.FormElement:
        case DynamicReferenceType.ObjectModelProperty:
        case DynamicReferenceType.ObjectModelRecordMetadata:
          $var.replaceWith(`<var>${formatter(evaluator.evaluateFormula(scope, { formula: "f_" + Formula.clientIdToFormulaId(data.id) }), data)}</var>`)
          break
        case DynamicReferenceType.Formula:
          $var.replaceWith(`<var>${formatter(evaluator.evaluateFormula(scope, { formula: data.formula }), data)}</var>`)
          break
        default:
          $var.remove()
          break
      }
    })
    return $snippet.get().map(el => el.outerHTML).join('')
  }

  /**
   * @see {FormSubmissionWysiwygValue.getValue}
   */
  getValue(snippet: string, scope: string): string {
    return FormSubmissionWysiwygValue.getValue(snippet, scope, this.opts)
  }
}